export type TradingChannelKind = "image" | "text";
export type TradingChannelMatchingMode =
	| "message-linked"
	| "pair-linked"
	| "stream-matched";
export type TradingChannelSamePairTradePolicy =
	| "single-active-per-pair"
	| "allow-multiple-active";
export type TradingChannelExecutionPolicy =
	| "complete-only"
	| "immediate-propagation";
export type TradingChannelParserRoutingMode = "llm-only" | "parser-enabled";
export type TradingChannelExtractionMode =
	| "auto"
	| "image-and-text"
	| "text-only";
export type TradingChannelTakeProfitPriceMode = "all" | "final" | "selected";

export const TRADING_CHANNEL_KIND_DISPLAY = {
	text: {
		label: "Message-linked",
		matchingMode: "message-linked",
		description:
			"Tracks follow-ups through the original Telegram message thread for that trade.",
	},
	image: {
		label: "Pair-linked",
		matchingMode: "pair-linked",
		description:
			"Tracks follow-ups by matching the trading pair inside the same Telegram chat.",
	},
} as const satisfies Record<
	TradingChannelKind,
	{
		label: string;
		matchingMode: "message-linked" | "pair-linked";
		description: string;
	}
>;

export const TRADING_CHANNEL_MATCHING_MODE_DISPLAY = {
	"message-linked": {
		label: "Message-linked",
		description:
			"Tracks follow-ups through the original Telegram message thread for that trade.",
		legacyKind: "text",
	},
	"pair-linked": {
		label: "Pair-linked",
		description:
			"Tracks follow-ups by matching the trading pair inside the same Telegram chat.",
		legacyKind: "image",
	},
	"stream-matched": {
		label: "Stream-matched",
		description:
			"Tracks conversational trade streams inside the chat, including standalone follow-ups that are not reliable reply-thread updates.",
		legacyKind: "image",
	},
} as const satisfies Record<
	TradingChannelMatchingMode,
	{
		label: string;
		description: string;
		legacyKind: TradingChannelKind;
	}
>;

export const TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY_DISPLAY = {
	"single-active-per-pair": {
		label: "One active trade",
		description:
			"Preserve legacy pair-linked behavior: a fresh same-pair setup can replace or update the active pair trade.",
	},
	"allow-multiple-active": {
		label: "Allow multiple",
		description:
			"Allow independent same-pair setups to stay active together; ambiguous management is blocked until SignalLens can scope it safely.",
	},
} as const satisfies Record<
	TradingChannelSamePairTradePolicy,
	{
		label: string;
		description: string;
	}
>;

export const TRADING_CHANNEL_EXTRACTION_MODE_DISPLAY = {
	auto: {
		label: "Auto",
		description:
			"Use the normal SignalLens extraction path for this chat and analyze images when image messages arrive.",
	},
	"image-and-text": {
		label: "Image + text",
		description:
			"Always analyze both the image and available text context for photo-based signals.",
	},
	"text-only": {
		label: "Text only",
		description:
			"Ignore image bytes for this chat and extract from the caption, reply text, and stream context only.",
	},
} as const satisfies Record<
	TradingChannelExtractionMode,
	{
		label: string;
		description: string;
	}
>;

export const normalizeTradingChannelMatchingMode = (
	value: unknown,
): TradingChannelMatchingMode | null => {
	if (
		value === "message-linked" ||
		value === "pair-linked" ||
		value === "stream-matched"
	) {
		return value;
	}

	return null;
};

export const normalizeTradingChannelSamePairTradePolicy = (
	value: unknown,
): TradingChannelSamePairTradePolicy | null => {
	if (value === "single-active-per-pair" || value === "allow-multiple-active") {
		return value;
	}

	return null;
};

export const normalizeTradingChannelExtractionMode = (
	value: unknown,
): TradingChannelExtractionMode | null => {
	if (value === "auto" || value === "image-and-text" || value === "text-only") {
		return value;
	}

	return null;
};

export const getLegacyTradingChannelKindForMatchingMode = (
	matchingMode: TradingChannelMatchingMode,
): TradingChannelKind =>
	TRADING_CHANNEL_MATCHING_MODE_DISPLAY[matchingMode].legacyKind;

export const getTradingChannelMatchingMode = (
	input:
		| TradingChannelKind
		| {
				kind: TradingChannelKind;
				matchingMode?: TradingChannelMatchingMode | null;
		  },
): TradingChannelMatchingMode =>
	typeof input === "string"
		? TRADING_CHANNEL_KIND_DISPLAY[input].matchingMode
		: (input.matchingMode ??
			TRADING_CHANNEL_KIND_DISPLAY[input.kind].matchingMode);

export const TRADING_CHANNEL_EXECUTION_POLICY_DISPLAY = {
	"complete-only": {
		label: "Complete-only",
		description:
			"Only opens a new trade after SignalLens has a complete executable setup.",
	},
	"immediate-propagation": {
		label: "Immediate-propagation",
		description:
			"Opens explicit market-entry commands immediately and propagates follow-up SL/TP updates onto the live trade as they arrive.",
	},
} as const satisfies Record<
	TradingChannelExecutionPolicy,
	{
		label: string;
		description: string;
	}
>;

export const normalizeTradingChannelExecutionPolicy = (
	value: unknown,
): TradingChannelExecutionPolicy | null => {
	if (value === "complete-only" || value === "immediate-propagation") {
		return value;
	}

	if (value === "immediate-protected") {
		return "immediate-propagation";
	}

	return null;
};

export const DEFAULT_TRADING_CHANNEL_SEND_TO_EA = true;
export const DEFAULT_TRADING_CHANNEL_FIXED_RISK_AMOUNT_PER_TRADE = null;
export const DEFAULT_TRADING_CHANNEL_PERCENTAGE_RISK_PER_TRADE = 1;
export const DEFAULT_TRADING_CHANNEL_AUTO_STOP_LOSS_THRESHOLD = 50;
export const DEFAULT_TRADING_CHANNEL_EXECUTION_POLICY = "complete-only";
export const DEFAULT_TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY =
	"single-active-per-pair" as const satisfies TradingChannelSamePairTradePolicy;
export const DEFAULT_TRADING_CHANNEL_PARSER_ROUTING_MODE = "llm-only";
export const DEFAULT_TRADING_CHANNEL_EXTRACTION_MODE = "auto";
export const DEFAULT_TRADING_CHANNEL_PARSER_ANALYSIS_LOOKBACK_COUNT = 1000;
export const DEFAULT_IMMEDIATE_ENTRY_EMERGENCY_STOP_LOSS_PERCENT = 2.0;
export const DEFAULT_ALLOW_IMMEDIATE_ENTRY_WITHOUT_TAKE_PROFIT = true;
export const DEFAULT_IMAGE_TAKE_PROFIT_LEVELS = [25, 55, 85] as const;
export const DEFAULT_IMAGE_TAKE_PROFIT_VOLUME_PCT = [20, 25, 55] as const;
export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_LEVELS =
	DEFAULT_IMAGE_TAKE_PROFIT_LEVELS.join(",");
export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_VOLUME_PCT =
	DEFAULT_IMAGE_TAKE_PROFIT_VOLUME_PCT.join(",");
export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE =
	"all" as const satisfies TradingChannelTakeProfitPriceMode;
export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_SELECTION = null;

export type TradingChannelTakeProfitExecutionMode =
	| "single-trade"
	| "separate-trades-per-tp";

export type TradingChannelTpSplitSizingMode =
	| "inherit"
	| "fixed-risk"
	| "risk-percent"
	| "lot-size"
	| "ea-default";

export type TradingChannelTpSplitNumericMode =
	| "inherit"
	| "value"
	| "ea-default";

export interface TradingChannelTpSplitChildOverride {
	tpNumber: number;
	sizingMode: TradingChannelTpSplitSizingMode;
	fixedRiskAmountPerTrade: number | null;
	percentageRiskPerTrade: number | null;
	lotSize: number | null;
	autoStopLossMode: TradingChannelTpSplitNumericMode;
	autoStopLossThreshold: number | null;
	marketEntryToleranceMode: TradingChannelTpSplitNumericMode;
	marketEntryTolerancePercent: number | null;
}

export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE =
	"single-trade" as const satisfies TradingChannelTakeProfitExecutionMode;
export const DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_SPLIT_CHILD_OVERRIDES =
	[] as const satisfies readonly TradingChannelTpSplitChildOverride[];

export const TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE_DISPLAY = {
	"single-trade": {
		label: "One trade with TP ladder",
		description:
			"Open one trade and let the EA manage the extracted TP ladder as it does today.",
	},
	"separate-trades-per-tp": {
		label: "One trade per TP",
		description:
			"Open a separate trade for each executable TP level. Risk settings apply to each child trade.",
	},
} as const satisfies Record<
	TradingChannelTakeProfitExecutionMode,
	{
		label: string;
		description: string;
	}
>;

export const TRADING_CHANNEL_TP_SPLIT_SIZING_MODE_DISPLAY = {
	inherit: {
		label: "Inherit",
		description: "Use the channel-level sizing setting for this TP child.",
	},
	"fixed-risk": {
		label: "Fixed risk",
		description: "Use a fixed account-currency risk amount for this TP child.",
	},
	"risk-percent": {
		label: "Risk %",
		description: "Use a percentage risk setting for this TP child.",
	},
	"lot-size": {
		label: "Lot size",
		description: "Use a fixed lot size for this TP child.",
	},
	"ea-default": {
		label: "EA default",
		description: "Clear SignalLens sizing overrides for this TP child.",
	},
} as const satisfies Record<
	TradingChannelTpSplitSizingMode,
	{
		label: string;
		description: string;
	}
>;

export const TRADING_CHANNEL_TP_SPLIT_NUMERIC_MODE_DISPLAY = {
	inherit: {
		label: "Inherit",
		description: "Use the channel-level value for this TP child.",
	},
	value: {
		label: "Value",
		description: "Use this TP child's configured value.",
	},
	"ea-default": {
		label: "EA default",
		description: "Clear the SignalLens override for this TP child.",
	},
} as const satisfies Record<
	TradingChannelTpSplitNumericMode,
	{
		label: string;
		description: string;
	}
>;

export const TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE_DISPLAY = {
	all: {
		label: "All parsed TPs",
		description:
			"Send every extracted TP price to the EA as staged explicit TP targets.",
	},
	final: {
		label: "Final TP only",
		description:
			"Send only the direction-aware final target as the normal broker TP.",
	},
	selected: {
		label: "Selected TP numbers",
		description: "Send only source-order TP numbers such as TP3 or TP2,TP3.",
	},
} as const satisfies Record<
	TradingChannelTakeProfitPriceMode,
	{
		label: string;
		description: string;
	}
>;
export type TradingChannelExecutableIntent =
	| "NOT_TAKEN"
	| "RE_ENTER"
	| "UPDATE_EXISTING_TRADE"
	| "SL_TO_ENTRY"
	| "PARTIAL_CLOSE"
	| "CLOSE_TRADE"
	| "CLOSE_ALL_TRADES"
	| "CANCEL_ORDER";

export const TRADING_CHANNEL_EXECUTABLE_INTENTS = [
	"NOT_TAKEN",
	"RE_ENTER",
	"UPDATE_EXISTING_TRADE",
	"SL_TO_ENTRY",
	"PARTIAL_CLOSE",
	"CLOSE_TRADE",
	"CLOSE_ALL_TRADES",
	"CANCEL_ORDER",
] as const satisfies readonly TradingChannelExecutableIntent[];

export const TRADING_CHANNEL_EXECUTABLE_INTENT_DISPLAY = {
	NOT_TAKEN: {
		label: "New trade / setup",
		description: "Ignore new setup messages for this chat.",
	},
	RE_ENTER: {
		label: "Re-entry / add trade",
		description: "Ignore re-entry or add-on trade instructions.",
	},
	UPDATE_EXISTING_TRADE: {
		label: "Update existing trade",
		description: "Ignore entry, SL, or TP edits for existing trades.",
	},
	SL_TO_ENTRY: {
		label: "Move SL to entry",
		description:
			"Ignore breakeven instructions, including configured entry-like SL updates.",
	},
	PARTIAL_CLOSE: {
		label: "Partial close",
		description: "Ignore instructions to close part of an active trade.",
	},
	CLOSE_TRADE: {
		label: "Close trade",
		description: "Ignore direct close or take-profit-now instructions.",
	},
	CLOSE_ALL_TRADES: {
		label: "Broad close-all command",
		description: "Ignore broad shutdown commands for supported paths.",
	},
	CANCEL_ORDER: {
		label: "Cancel pending order",
		description: "Ignore pending-order cleanup and cancel instructions.",
	},
} as const satisfies Record<
	TradingChannelExecutableIntent,
	{
		label: string;
		description: string;
	}
>;

export const DEFAULT_TRADING_CHANNEL_IGNORED_EXECUTION_INTENTS =
	[] as const satisfies readonly TradingChannelExecutableIntent[];

export interface TradingChannelSlToEntryUpdateDetection {
	enabled: boolean;
	favorableRiskDistancePercent: number;
	entryPricePercentFallback: number;
}

export const DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION: TradingChannelSlToEntryUpdateDetection =
	{
		enabled: true,
		favorableRiskDistancePercent: 0,
		entryPricePercentFallback: 0,
	};

export const TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS =
	{
		min: 0,
		max: 100,
	} as const;

export const TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS = {
	min: 0,
	max: 5,
} as const;
export const TRADING_CHANNEL_BREAKEVEN_AT_TP_LEVEL_OPTIONS = [
	1, 2, 3, 4,
] as const;
export type TradingChannelBreakevenAtTpLevel =
	(typeof TRADING_CHANNEL_BREAKEVEN_AT_TP_LEVEL_OPTIONS)[number];

export type TradingChannelPairFilterMode = "off" | "include" | "exclude";

export interface TradingChannelConfig {
	chatId: number | null;
	kind: TradingChannelKind;
	matchingMode?: TradingChannelMatchingMode;
	samePairTradePolicy?: TradingChannelSamePairTradePolicy;
	extractionMode?: TradingChannelExtractionMode;
	executionPolicy?: TradingChannelExecutionPolicy;
	parserRoutingMode?: TradingChannelParserRoutingMode;
	parserAnalysisLookbackCount?: number;
	pairAliases?: Record<string, string> | null;
	pairFilterMode?: TradingChannelPairFilterMode;
	pairIncludeList?: string[] | null;
	pairExcludeList?: string[] | null;
	title: string;
	sendToEa: boolean;
	ignoredExecutionIntents: TradingChannelExecutableIntent[];
	slToEntryUpdateDetection: TradingChannelSlToEntryUpdateDetection;
	fixedRiskAmountPerTrade: number | null;
	percentageRiskPerTrade: number | null;
	lotSize: number | null;
	autoStopLossThreshold: number | null;
	immediateEntryEmergencyStopLossPercent: number;
	allowImmediateEntryWithoutTakeProfit: boolean;
	breakevenAtTpLevel: TradingChannelBreakevenAtTpLevel | null;
	takeProfitLevels: string | null;
	takeProfitVolumePct: string | null;
	takeProfitExecutionMode?: TradingChannelTakeProfitExecutionMode;
	takeProfitSplitChildOverrides: TradingChannelTpSplitChildOverride[];
	takeProfitPriceMode?: TradingChannelTakeProfitPriceMode;
	takeProfitPriceSelection?: string | null;
	marketEntryTolerancePercent: number | null;
}

export const getTradingChannelExtractionMode = (
	input: Pick<TradingChannelConfig, "extractionMode"> | null | undefined,
): TradingChannelExtractionMode =>
	input?.extractionMode ?? DEFAULT_TRADING_CHANNEL_EXTRACTION_MODE;

export const normalizeTradingChannelParserRoutingMode = (
	value: unknown,
): TradingChannelParserRoutingMode | null => {
	if (value === "llm-only" || value === "parser-enabled") {
		return value;
	}
	return null;
};

export const normalizeTradingChannelTakeProfitPriceMode = (
	value: unknown,
): TradingChannelTakeProfitPriceMode | null => {
	if (value === "all" || value === "final" || value === "selected") {
		return value;
	}
	return null;
};

export const normalizeTradingChannelTakeProfitExecutionMode = (
	value: unknown,
): TradingChannelTakeProfitExecutionMode | null => {
	if (value === "single-trade" || value === "separate-trades-per-tp") {
		return value;
	}
	return null;
};

export const normalizeTradingChannelTpSplitSizingMode = (
	value: unknown,
): TradingChannelTpSplitSizingMode | null => {
	if (
		value === "inherit" ||
		value === "fixed-risk" ||
		value === "risk-percent" ||
		value === "lot-size" ||
		value === "ea-default"
	) {
		return value;
	}
	return null;
};

export const normalizeTradingChannelTpSplitNumericMode = (
	value: unknown,
): TradingChannelTpSplitNumericMode | null => {
	if (value === "inherit" || value === "value" || value === "ea-default") {
		return value;
	}
	return null;
};

export const parseTradingChannelTakeProfitPriceSelection = (
	value: string | null | undefined,
): number[] => {
	if (value === null || value === undefined) {
		return [];
	}

	const seen = new Set<number>();
	const parsed: number[] = [];
	for (const rawPart of value.split(",")) {
		const part = rawPart.trim();
		if (part === "") {
			continue;
		}
		if (!/^\d+$/.test(part)) {
			return [];
		}
		const tpNumber = Number(part);
		if (!Number.isInteger(tpNumber) || tpNumber <= 0) {
			return [];
		}
		if (!seen.has(tpNumber)) {
			seen.add(tpNumber);
			parsed.push(tpNumber);
		}
	}

	return parsed.sort((left, right) => left - right);
};

export const normalizeTradingChannelTakeProfitPriceSelection = (
	value: unknown,
): string | null => {
	if (value === null || value === undefined) {
		return DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_SELECTION;
	}

	const raw =
		typeof value === "string"
			? value
			: Array.isArray(value)
				? value.join(",")
				: "";
	const parsed = parseTradingChannelTakeProfitPriceSelection(raw);
	return parsed.length > 0 ? parsed.join(",") : null;
};

export const isTradingChannelExecutableIntent = (
	value: unknown,
): value is TradingChannelExecutableIntent =>
	typeof value === "string" &&
	(TRADING_CHANNEL_EXECUTABLE_INTENTS as readonly string[]).includes(value);

export const normalizeTradingChannelIgnoredExecutionIntents = (
	value: unknown,
): TradingChannelExecutableIntent[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	const seen = new Set<TradingChannelExecutableIntent>();
	const normalized: TradingChannelExecutableIntent[] = [];
	for (const item of value) {
		if (!isTradingChannelExecutableIntent(item) || seen.has(item)) {
			continue;
		}
		seen.add(item);
		normalized.push(item);
	}
	return normalized;
};

const normalizeFinitePercentWithinBounds = (
	value: unknown,
	bounds: { min: number; max: number },
	fallback: number,
) => {
	const parsed =
		typeof value === "number"
			? value
			: typeof value === "string" && value.trim() !== ""
				? Number(value)
				: Number.NaN;
	if (!Number.isFinite(parsed) || parsed < bounds.min || parsed > bounds.max) {
		return fallback;
	}
	return parsed;
};

export const normalizeTradingChannelSlToEntryUpdateDetection = (
	value: unknown,
): TradingChannelSlToEntryUpdateDetection => {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return { ...DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION };
	}

	const record = value as Partial<
		Record<keyof TradingChannelSlToEntryUpdateDetection, unknown>
	>;
	return {
		enabled:
			typeof record.enabled === "boolean"
				? record.enabled
				: DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION.enabled,
		favorableRiskDistancePercent: normalizeFinitePercentWithinBounds(
			record.favorableRiskDistancePercent,
			TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS,
			DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION.favorableRiskDistancePercent,
		),
		entryPricePercentFallback: normalizeFinitePercentWithinBounds(
			record.entryPricePercentFallback,
			TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS,
			DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION.entryPricePercentFallback,
		),
	};
};

export const shouldExecuteTradingChannelIntent = (
	ignoredExecutionIntents: readonly TradingChannelExecutableIntent[],
	intent: TradingChannelExecutableIntent | null | undefined,
) =>
	intent === null ||
	intent === undefined ||
	!ignoredExecutionIntents.includes(intent);

export const normalizePairFilterMode = (
	value: unknown,
): TradingChannelPairFilterMode => {
	if (value === "include" || value === "exclude") {
		return value;
	}
	return "off";
};

export const normalizePairFilterList = (value: unknown): string[] | null => {
	if (!value || !Array.isArray(value)) {
		return null;
	}
	const normalized = value
		.map((item) => (typeof item === "string" ? item.trim().toUpperCase() : ""))
		.filter((item) => item !== "");
	return normalized.length > 0 ? normalized : null;
};

export const shouldDispatchPairForChannel = (
	pair: string | null,
	config: Pick<
		TradingChannelConfig,
		"pairFilterMode" | "pairIncludeList" | "pairExcludeList"
	>,
): boolean => {
	const mode = config.pairFilterMode ?? "off";
	if (mode === "off" || !pair) {
		return true;
	}
	const normalizedPair = pair.trim().toUpperCase();
	if (mode === "include") {
		const list = config.pairIncludeList;
		if (!list || list.length === 0) {
			return true;
		}
		return list.includes(normalizedPair);
	}
	if (mode === "exclude") {
		const list = config.pairExcludeList;
		if (!list || list.length === 0) {
			return true;
		}
		return !list.includes(normalizedPair);
	}
	return true;
};

// ----- Staged take-profit validation (shared between backend + renderer) -----

export type StagedTakeProfitValidationError =
	| "trigger_invalid"
	| "volume_invalid"
	| "length_mismatch"
	| "volume_sum_not_100"
	| "mutually_inclusive";

export interface StagedTakeProfitValidationResult {
	triggers: string | null;
	volumes: string | null;
	error: StagedTakeProfitValidationError | null;
}

export const STAGED_TAKE_PROFIT_VALIDATION_MESSAGES: Record<
	StagedTakeProfitValidationError,
	string
> = {
	trigger_invalid:
		"Trigger % must be comma-separated whole numbers between 1 and 100 (e.g. 25,55,85).",
	volume_invalid:
		"Volume % must be comma-separated whole numbers between 1 and 100 (e.g. 20,25,55).",
	length_mismatch:
		"Trigger % and Volume % must have the same number of values.",
	volume_sum_not_100: "Volume % values must add up to exactly 100.",
	mutually_inclusive:
		"Trigger % and Volume % must be set together, or both cleared to disable staged take-profit.",
};

const normalizeStagedTakeProfitCsvInput = (rawValue: string) =>
	rawValue
		.split(",")
		.map((item) => item.trim())
		.filter((item) => item !== "")
		.join(",");

const parseStagedTakeProfitCsvIntegers = (
	normalized: string,
): number[] | null => {
	if (normalized === "") {
		return null;
	}

	const parts = normalized.split(",");
	const parsed: number[] = [];
	for (const part of parts) {
		if (!/^\d+$/.test(part)) {
			return null;
		}
		const value = Number(part);
		if (!Number.isInteger(value) || value <= 0 || value > 100) {
			return null;
		}
		parsed.push(value);
	}
	return parsed.length > 0 ? parsed : null;
};

/**
 * Pure validator for the staged take-profit pair (trigger levels + volume
 * distribution). Mirrors the backend normalizer in
 * `src/config/take-profit-levels-settings.ts` so the renderer can preview
 * errors without round-tripping to disk.
 */
export const validateStagedTakeProfitPair = (
	rawTriggers: string,
	rawVolumes: string,
): StagedTakeProfitValidationResult => {
	const triggers = normalizeStagedTakeProfitCsvInput(rawTriggers);
	const volumes = normalizeStagedTakeProfitCsvInput(rawVolumes);

	if (triggers === "" && volumes === "") {
		return { triggers: null, volumes: null, error: null };
	}

	if (triggers === "" || volumes === "") {
		return {
			triggers: triggers === "" ? null : triggers,
			volumes: volumes === "" ? null : volumes,
			error: "mutually_inclusive",
		};
	}

	const parsedTriggers = parseStagedTakeProfitCsvIntegers(triggers);
	if (parsedTriggers === null) {
		return { triggers, volumes, error: "trigger_invalid" };
	}

	const parsedVolumes = parseStagedTakeProfitCsvIntegers(volumes);
	if (parsedVolumes === null) {
		return { triggers, volumes, error: "volume_invalid" };
	}

	if (parsedTriggers.length !== parsedVolumes.length) {
		return { triggers, volumes, error: "length_mismatch" };
	}

	const volumeSum = parsedVolumes.reduce((acc, value) => acc + value, 0);
	if (volumeSum !== 100) {
		return { triggers, volumes, error: "volume_sum_not_100" };
	}

	return { triggers, volumes, error: null };
};
